import { LoadState, NullableLoadState } from '@treadinc/horizon-api-spec';

// The four load-status buckets shown on the Gantt, plus a distinct UNKNOWN
// treatment for "no signal" (GPS gaps, null idle, non-cycle states). UNKNOWN is
// rendered with a hatch pattern so it never reads as solid Idle — idle must
// not be overstated.
export type ReplayBucket = 'EN_ROUTE' | 'AT_PICKUP' | 'AT_DROPOFF' | 'IDLE' | 'UNKNOWN';

// Gantt bucket colors — match the companion design mockup
// (06-18-26_truck-replay-tab-mockup.html). UNKNOWN's swatch is the base color
// the Gantt hatches over. IDLE uses the same yellow as the existing job-route
// ping viewer's idle overlay (LiveMap Timeline IdleSegment) so idle reads
// consistently across the two surfaces.
const BUCKET_COLORS: Record<ReplayBucket, string> = {
  AT_DROPOFF: '#00A11A',
  AT_PICKUP: '#FFAA13',
  EN_ROUTE: '#0074DF',
  IDLE: '#FDE68A',
  UNKNOWN: '#AEB9BF',
};

export function getReplayBucketColor(bucket: ReplayBucket) {
  return BUCKET_COLORS[bucket];
}

/**
 * Maps a ping's load state to a Gantt bucket.
 *
 * Site presence takes precedence over the idle signal: a truck parked at the
 * pickup or dropoff site is *expected* to idle there, so we always show the
 * site bucket (AT_PICKUP / AT_DROPOFF) rather than IDLE — otherwise the dwell
 * at a site reads as idle and obscures the at-site windows. This holds even
 * when `userIdleRaw === null` (idle detection never ran): at a site, load state
 * is ground truth, so a null-idle ping at a site is still site dwell, not
 * UNKNOWN. Idle is therefore only ever surfaced off-site (en route / off-cycle).
 *
 * Away from a site:
 * - `userIdleRaw === true`  → IDLE
 * - `userIdleRaw === null`  → UNKNOWN (no idle signal — never assume idle)
 * - otherwise the bucket is derived from `loadState`.
 *
 * En-route to pickup and to dropoff are intentionally collapsed into one
 * EN_ROUTE bucket (validated with the exec, 06-18-26).
 */
export function loadStateToReplayBucket(
  loadState: NullableLoadState,
  userIdleRaw: boolean | null,
): ReplayBucket {
  const atPickupSite =
    loadState === LoadState.ARRIVED_PICKUP || loadState === LoadState.LOADED;
  const atDropoffSite =
    loadState === LoadState.ARRIVED_DROPOFF || loadState === LoadState.UNLOADED;

  if (atPickupSite) return 'AT_PICKUP';
  if (atDropoffSite) return 'AT_DROPOFF';

  if (userIdleRaw === true) return 'IDLE';
  if (userIdleRaw === null) return 'UNKNOWN';

  switch (loadState) {
    case LoadState.TO_PICKUP:
    case LoadState.TO_DROPOFF:
      return 'EN_ROUTE';
    case LoadState.CREATED:
    case LoadState.COMPLETED:
    case LoadState.CANCELED:
    case null:
      // States outside the active pickup→dropoff cycle carry no activity color.
      return 'UNKNOWN';
    default:
      throw new Error(`Unhandled load state: ${loadState}`);
  }
}
